import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CircleCheck, Share2 } from 'lucide-react';

import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { font } from 'settings/tokens';
import { stateCardSurfaceSx } from './stateCardSurface';


type MissingStatus = 'lost' | 'stolen';

interface MissingStateCalloutProps {
  status: MissingStatus;
  /** Only rendered for `stolen` — see the note on SHARE below. */
  onShare?: () => void;
  /**
   * Ends the missing state. Rendered for BOTH statuses — it is the one action
   * every missing item needs, and it lives in this card rather than in a pinned
   * bar. See the note above the button.
   */
  onReportFound?: () => void;
}

/**
 * VERA-301 (Lost & Stolen Statuses) — the owner-facing block shown while a
 * certificate is reported lost or stolen.
 *
 * WHY A CARD, not a plain section (which is what Figma `2235:939936` /
 * `2235:940160` draw):
 *
 * This is a *temporary state*. Everything else on the certificate page is
 * permanent truth about the object; this block exists only while the item is
 * missing and vanishes when it is found. A container says "this is an overlay
 * on your certificate, not part of it."
 *
 * THE CARD IS NEUTRAL; THE CHIP CARRIES STATUS. Two coloured treatments were
 * tried and rejected on sight, in this order:
 *   1. A status TINT on the whole panel (red for stolen, orange for lost). At
 *      375px it made the white outlined buttons read as cut-out holes rather
 *      than controls, and it fought the grey visibility pill beneath it.
 *   2. A 3px left ACCENT BAR. Still read as decoration competing with the chip.
 * Do not re-add either. Status colour belongs on the chip in the heading row —
 * one coloured element, which is also the one the user already reads as "the
 * state". The card is a grey container; that is the whole point.
 *
 * Heading row = title left, chip right (`justifyContent='space-between'`), the
 * same pattern VERA-304 uses for label + chip in `OwnerProvenanceCard`.
 *
 * SHARE is deliberately stolen-only. The capability is not missing on lost —
 * the page header carries a share icon on every certificate. The stolen state
 * does not *add* Share, it *promotes* it, because its copy gives a specific
 * reason to ("share with law enforcement"). Lost has no such call to action, so
 * a Share button there would be an unmotivated control competing with the one
 * action that matters: Report found. If lost ever warrants promoting it, add
 * the sentence first and let the button follow.
 *
 * Extracted per working-conventions §36 (one hand-roll allowed, the second is a
 * shared component) — the lost and stolen blocks were the same structure twice.
 */
export const MissingStateCallout = ({
  status,
  onShare,
  onReportFound,
}: MissingStateCalloutProps) => {
  const isStolen = status === 'stolen';

  // Copy rule: never narrate a button that is already on screen. The previous
  // stolen body spent two of its three clauses restating the "Stolen" chip and
  // telling the user to click "Report found" — 24 words to say one thing. Lost
  // needs no body at all: the heading asks the question, the button answers it.
  const copy = isStolen
    ? {
        // Replaces "Security message", which was a category label — it described
        // what kind of message this is, not what to do. This heading covers BOTH
        // actions (recover it yourself, or get law enforcement involved).
        heading: 'Recover this item',
        body: 'Share the certificate and its scan history with law enforcement.',
      }
    : {
        heading: 'Found your item?',
        body: null,
      };

  return (
    <Box
      /*
       * VERA-315 — one shared surface with the pending-transfer card
       * (`TransferSummarySection`); the reasoning lives in `stateCardSurface`.
       *
       * This replaces a hand-written `border: 1px solid grey[300]` + white fill, and
       * both halves of that were not what they looked like:
       *
       *  - the FILL was `background.paper` = `#FFFFFF` on a `#FFFFFF` page, so the
       *    card measured 1.00:1 against what it sat on;
       *  - the BORDER never rendered `grey[300]`. `index.css` sets
       *    `#vera-shadow-root * { border-color: hsl(var(--border)) }`, which has ID
       *    specificity and beats emotion's sx class, so the authored colour was
       *    silently discarded and the edge drew at `border.subtle` `#E4E4E7`
       *    (confirmed by computed style on the live page). See the note on the
       *    `border` token in `settings/tokens.ts`.
       *
       * So the two cards were not a border-vs-no-border judgement, they were one
       * deliberate choice and one that had never taken effect.
       */
      sx={stateCardSurfaceSx}
    >
      <Stack spacing={1.5}>
        {/* Heading left, status chip RIGHT — the same space-between row VERA-304
            uses for label + chip (OwnerProvenanceCard). The chip is what carries
            status colour here, which is why the card itself stays neutral. */}
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
          {/* `font.body` / 500 — the state-card family's one heading treatment. It
              spent part of 2026-08-30 at `font.heading` / 600 and was reverted with
              its three siblings; the reasoning lives in `tranferSummary.tsx` beside
              the card that prompted both changes. Never change this alone. */}
          <Typography sx={{ fontSize: font.body, fontWeight: 500 }}>{copy.heading}</Typography>
          <Box sx={{ flexShrink: 0 }}>
            <StatusBadge status={status} />
          </Box>
        </Stack>
        {/* lineHeight 1.5 so descriptive copy doesn't read dense (§15). Lost has
            no body — heading + button say it all. */}
        {copy.body && (
          <Typography sx={{ fontSize: font.label, lineHeight: 1.5 }} color='text.secondary'>
            {copy.body}
          </Typography>
        )}
        {/* A TEMPORARY STATE'S BUTTONS LIVE IN ITS BLOCK. Both controls sit in
            this card, full width, stacked.

            `Report found` was moved OUT to the page's pinned bar in VERA-315
            Phase 1, on the argument that the state's primary action should be
            reachable without scrolling. That was reverted here (2026-08-22, user
            directive: "always temporary state buttons are in the block"), and the
            same reversal had ALREADY been made for the pending-gift card — see
            GiftOutActionsSection, which moved `Cancel gift` back off the sticky
            bar because "the explanation and the action that resolves it belong
            together; separating them was the defect". This card was simply the
            one that never got the same treatment, so the two temporary-state
            cards disagreed. They now match.

            What the pinned bar actually cost: it could only ever show the VERB.
            For a stolen item that is the least useful half — the status chip and
            the reason to involve law enforcement are up here, and the button that
            ends the state was a full card away from them.

            HIERARCHY — Share is secondary, `Report found` is primary and comes
            LAST (§26/§33: one primary per surface, and in a vertical stack "last"
            is the bottom). Note this deliberately differs from the gift card,
            whose single action is secondary: `Cancel gift` is a REVERSAL, which a
            solid fill oversells, whereas `Report found` is the affirmative action
            that resolves this state. Different jobs, different weight. */}
        <Stack spacing={1} mt={1}>
          {isStolen && onShare && (
            <Button
              fullWidth
              variant='outlined'
              startIcon={<Share2 size={18} />}
              onClick={onShare}
              sx={{
                textTransform: 'none',
                py: 1.25,
                backgroundColor: theme => theme.palette.common.white,
                borderColor: theme => theme.palette.grey[300],
              }}
            >
              Share
            </Button>
          )}
          {onReportFound && (
            <Button
              fullWidth
              variant='contained'
              startIcon={<CircleCheck size={18} />}
              onClick={onReportFound}
              sx={{ textTransform: 'none', py: 1.25 }}
            >
              Report found
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
